import { useState } from "react";
import { Button, Popconfirm, Space, Tooltip, message } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

const useRoleManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [roles, setRoles] = useState({
    data: [
      {
        id: 1,
        roleName: "Admin",
        roleDescription: "Full access",
        permissions: ["read", "write", "delete"],
      },
      {
        id: 2,
        roleName: "Editor",
        roleDescription: "Can edit content",
        permissions: ["read", "write"],
      },
      {
        id: 3,
        roleName: "Trainee",
        roleDescription: "Can view content",
        permissions: ["read"],
      },
      {
        id: 4,
        roleName: "Team Leader",
        roleDescription: "Can accept team skills",
        permissions: ["read", "write"],
      },
      {
        id: 5,
        roleName: "Project Manager",
        roleDescription: "Can manage projects",
        permissions: ["read", "write"],
      },
      {
        id: 6,
        roleName: "DivisionManager",
        roleDescription: "Can manage divisions",
        permissions: ["read", "write"],
      },
    ],
    meta: {
      page: 1,
      take: 10,
      itemCount: 2,
    },
  });
  const [editingKey, setEditingKey] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  // Define available permissions
  const availablePermissions = ["read", "write", "delete"];

  // Add a new role
  const handleAddRole = (newRole) => {
    setRoles((prevRoles) => ({
      ...prevRoles,
      data: [...prevRoles.data, { id: Date.now(), ...newRole }],
    }));
    message.success("Role added successfully");
    setIsAddModalVisible(false);
  };

  // Edit an existing role
  const handleEditRole = () => {
    if (editingRole) {
      setRoles((prevRoles) => ({
        ...prevRoles,
        data: prevRoles.data.map((role) =>
          role.id === editingRole.id ? editingRole : role
        ),
      }));
      setEditingKey(null);
      message.success("Role updated successfully");
      setIsEditModalVisible(false);
    }
  };

  // Delete a role
  const handleDelete = (id) => {
    setRoles((prevRoles) => ({
      ...prevRoles,
      data: prevRoles.data.filter((role) => role.id !== id),
    }));
    message.success("Role deleted successfully");
  };

  const onChange = (pagination) => {
    const { current, pageSize } = pagination;
    setRoles((prevRoles) => ({
      ...prevRoles,
      meta: {
        ...prevRoles.meta,
        page: current,
        take: pageSize,
      },
    }));
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    setRoles((prevRoles) => ({
      ...prevRoles,
      data: prevRoles.data.filter(
        (role) =>
          role.roleName.toLowerCase().includes(query) ||
          role.roleDescription.toLowerCase().includes(query)
      ),
    }));
  };

  const openEditModal = (role) => {
    setEditingRole({ ...role });
    setIsEditModalVisible(true);
  };

  // Columns definition
  const columns = [
    {
      title: "Role Name",
      dataIndex: "roleName",
      key: "roleName",
    },
    {
      title: "Description",
      dataIndex: "roleDescription",
      key: "roleDescription",
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      render: (permissions) => permissions.join(", "), // Show permissions as a comma-separated list
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="middle">
          <Tooltip title="Edit Role">
            <Button type="primary" onClick={() => openEditModal(record)}>Edit</Button>
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this role?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Delete Role">
              <Button type="default" danger>Delete</Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return {
    roles,
    isFetching: false,
    error: null,
    columns,
    onChange,
    handleSearchChange,
    handleAddRole,
    handleEditRole,
    rowClassName: (record) => (record.status === "Pending" ? "pending-row" : ""),
    isAddModalVisible,
    setIsAddModalVisible,
    isEditModalVisible,
    setIsEditModalVisible,
    editingRole,
    setEditingRole,
    availablePermissions,
  };
};

export default useRoleManagement;